import { IDashboardAccessSetting } from "./useDashboardQueries";

// widget with no configured access entry (or an empty permissions list) is
// treated as NOT accessible, so it shows the permission message and loads no
// data. The backend access setting must list every widget the user should see.
export function hasWidgetPermission(
  widgetId: string,
  dashboardId: string,
  dashboardAccessSetting: IDashboardAccessSetting[],
  userPermSet: Set<string>
): boolean {
  const dashboardAccess = dashboardAccessSetting?.find(
    (d) => d.dashboardId === dashboardId
  );
  const widgetAccess = dashboardAccess?.widgets?.find((w) => w.id === widgetId);
  const required = widgetAccess?.permissions;
  if (required && required.length > 0) {
    return required.every((perm) => userPermSet.has(perm.toUpperCase()));
  }
  return false;
}

// Annotate every widget with `hasPermission` instead of removing the ones the
// user can't access, so the UI can keep rendering the widget and show an
// in-widget message for the ones without the required permission.
export function annotateWidgetsWithPermission<T extends { id: string }>(
  widgets: T[],
  dashboardId: string,
  dashboardAccessSetting: IDashboardAccessSetting[],
  userPermissions: string[]
): Array<T & { hasPermission: boolean }> {
  const userPermSet = new Set((userPermissions || []).map((p) => p.toUpperCase()));
  return widgets.map((widget) => ({
    ...widget,
    hasPermission: hasWidgetPermission(
      widget.id,
      dashboardId,
      dashboardAccessSetting,
      userPermSet
    ),
  }));
}

export function filterWidgetsByPermission<T extends { id: string }>(
  widgets: T[],
  dashboardId: string,
  dashboardAccessSetting: IDashboardAccessSetting[],
  userPermissions: string[]
): T[] {
  return annotateWidgetsWithPermission(
    widgets,
    dashboardId,
    dashboardAccessSetting,
    userPermissions
  ).filter((w) => w.hasPermission);
}
